/**
 * AgendaWizard Component
 * Right-side wizard drawer for building a personalized learning agenda.
 * Questions are loaded from the DB (public endpoint) so admins can edit
 * labels/descriptions without a code deploy.
 * Opens and completions are tracked via /api/agenda-wizard/track.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import ApiService from '../services/ApiService';
import './AgendaWizard.css';

// Fallback questions used if the API call fails
const DEFAULT_QUESTIONS = [
  {
    id: 'q1', step_key: 'q1', sort_order: 10,
    label: 'Which categories interest you?',
    sublabel: 'Select one or more (or leave all unchecked for any category)',
    placeholder: null, question_type: 'categories',
  },
  {
    id: 'q2', step_key: 'q2', sort_order: 20,
    label: 'When are you available?',
    sublabel: 'Filter sessions by start time (CET). Leave blank for all day.',
    placeholder: null, question_type: 'time_window',
  },
  {
    id: 'q3', step_key: 'q3', sort_order: 30,
    label: "What's your main focus for the day?",
    sublabel: 'Optional — describe your interest in a few words to get more relevant recommendations',
    placeholder: 'e.g. AI tools for product development, leadership skills, sustainability strategy...',
    question_type: 'text',
  },
];

const SEGMENT_DISPLAY_NAMES = {
  GROW_PLM: 'Grow@PLM', OPS_SW: 'OPS-SW', LCS: 'LCS', ME: 'ME', PES: 'PES', SIM: 'SIM'
};

const normalize24HourTimeInput = (value) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
  if (digitsOnly.length <= 2) return digitsOnly;
  return `${digitsOnly.slice(0, 2)}:${digitsOnly.slice(2)}`;
};

const isValid24HourTime = (time) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

function formatSlotInLocalTime(timeCET, sessionDate) {
  // timeCET: "HH:MM-HH:MM"  sessionDate: "DD-MM-YYYY"
  try {
    const [start, end] = timeCET.split('-');
    const [day, month, year] = sessionDate.split('-');

    const toLocal = (hhmm) => {
      const [h, m] = hhmm.split(':').map(Number);
      return DateTime.fromObject(
        { year: +year, month: +month, day: +day, hour: h, minute: m },
        { zone: 'Europe/Paris' }
      ).toLocal().toFormat('HH:mm');
    };

    const localStart = toLocal(start);
    const localEnd = toLocal(end);

    const isCET = DateTime.local().offset === DateTime.now().setZone('Europe/Paris').offset;

    return isCET
      ? `${start} – ${end} CET`
      : `${localStart} – ${localEnd} (local)  ·  ${start} – ${end} CET`;
  } catch {
    return timeCET; // safe fallback
  }
}

function buildAgenda(sessions, { selectedCategories, startTime, endTime, interest }) {

  // 1. Extract keywords (words > 2 chars, lowercased). Empty if no interest text.
  const keywords = interest.trim().length > 0
    ? interest.trim().split(/\s+/).filter(w => w.length > 2).map(w => w.toLowerCase())
    : [];

  // 2. Score a single session
  function scoreSession(session) {
    if (keywords.length === 0) return 0;
    let score = 0;
    const name = (session.name || '').toLowerCase();
    const desc = (session.description || '').toLowerCase();
    const cat = (session.category || '').toLowerCase();
    const pres = (session.presenter || '').toLowerCase();
    for (const kw of keywords) {
      if (name.includes(kw)) score += 3;
      if (desc.includes(kw)) score += 2;
      if (cat.includes(kw)) score += 2;
      if (pres.includes(kw)) score += 1;
    }
    return score;
  }

  // 3. Time window filter (compares session CET start time against user CET inputs)
  function toMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  }
  function isInWindow(session) {
    if (!session.timeCET) return false;
    const sessionStart = session.timeCET.split('-')[0];
    const sessionMins = toMinutes(sessionStart);
    const hasStart = isValid24HourTime(startTime);
    const hasEnd = isValid24HourTime(endTime);
    if (hasStart && hasEnd) return sessionMins >= toMinutes(startTime) && sessionMins <= toMinutes(endTime);
    if (hasStart) return sessionMins >= toMinutes(startTime);
    if (hasEnd) return sessionMins <= toMinutes(endTime);
    return true;
  }

  // 4. Category filter (empty array = all categories pass)
  function matchesCategory(session) {
    if (selectedCategories.length === 0) return true;
    return selectedCategories.includes(session.category);
  }

  // 5. Filter + score all sessions
  const scored = sessions
    .filter(s => matchesCategory(s) && isInWindow(s))
    .map(s => ({ ...s, _score: scoreSession(s) }));

  // 6. Sort chronologically by timeCET start, then by score descending
  scored.sort((a, b) => {
    const timeA = (a.timeCET || '').split('-')[0];
    const timeB = (b.timeCET || '').split('-')[0];
    if (timeA !== timeB) return timeA.localeCompare(timeB);
    return b._score - a._score;
  });

  // 7. Group by exact timeCET slot, keep top 2 per slot
  const slotMap = new Map();
  for (const s of scored) {
    const slot = s.timeCET || 'unknown';
    if (!slotMap.has(slot)) slotMap.set(slot, []);
    if (slotMap.get(slot).length < 2) slotMap.get(slot).push(s);
  }

  // 8. Return array of { slot, sessions } in chronological order
  return [...slotMap.entries()].map(([slot, sessions]) => ({ slot, sessions }));
}

const AgendaWizard = ({ isOpen, onClose, sessions = [], categories = [], categoryColors = {} }) => {
  const [questions, setQuestions] = useState(null); // null = not yet loaded
  const [stepIndex, setStepIndex] = useState(0);
  const [showResults, setShowResults] = useState(false);

  // Per-type answer state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [timeWindow, setTimeWindow] = useState({ startTime: '', endTime: '' });
  const [textAnswers, setTextAnswers] = useState({}); // { [step_key]: string }

  const [agenda, setAgenda] = useState([]);
  const trackedOpen = useRef(false);

  // Fetch questions & track open when wizard becomes visible
  useEffect(() => {
    if (!isOpen) {
      trackedOpen.current = false;
      return undefined;
    }

    if (!trackedOpen.current) {
      trackedOpen.current = true;
      ApiService.post('/agenda-wizard/track', { event_type: 'opened' }).catch(() => {});
    }

    if (questions === null) {
      ApiService.get('/agenda-wizard/questions')
        .then(data => {
          const rows = data?.data;
          setQuestions(rows && rows.length > 0 ? rows : DEFAULT_QUESTIONS);
        })
        .catch(() => setQuestions(DEFAULT_QUESTIONS));
    }

    const handleEsc = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, questions]);

  const resetWizard = () => {
    setStepIndex(0);
    setShowResults(false);
    setSelectedCategories([]);
    setTimeWindow({ startTime: '', endTime: '' });
    setTextAnswers({});
    setAgenda([]);
    setQuestions(null); // re-fetch on next open in case admin edited them
  };

  // Sort questions by sort_order (backend already does this, but be safe)
  const activeQuestions = questions
    ? [...questions].sort((a, b) => a.sort_order - b.sort_order)
    : null;

  const currentQuestion = activeQuestions?.[stepIndex] ?? null;
  const totalSteps = activeQuestions?.length ?? DEFAULT_QUESTIONS.length;
  const isFirstStep = stepIndex === 0;
  const isLastStep = activeQuestions != null && stepIndex === activeQuestions.length - 1;

  const invalidStart = timeWindow.startTime.trim().length > 0 && !isValid24HourTime(timeWindow.startTime);
  const invalidEnd = timeWindow.endTime.trim().length > 0 && !isValid24HourTime(timeWindow.endTime);
  const hasTimeError = invalidStart || invalidEnd;

  const handleNext = () => {
    if (!activeQuestions) return;
    if (!isLastStep) {
      setStepIndex(i => i + 1);
      return;
    }
    // Combine all text answers for keyword scoring
    const interest = activeQuestions
      .filter(q => q.question_type === 'text')
      .map(q => textAnswers[q.step_key] || '')
      .filter(Boolean)
      .join(' ');

    const nextAgenda = buildAgenda(sessions, {
      selectedCategories,
      startTime: timeWindow.startTime,
      endTime: timeWindow.endTime,
      interest,
    });
    setAgenda(nextAgenda);
    setShowResults(true);
    ApiService.post('/agenda-wizard/track', { event_type: 'completed' }).catch(() => {});
  };

  const handleBack = () => {
    if (stepIndex > 0) setStepIndex(i => i - 1);
  };

  // Category helpers
  const allCategoriesSelected = categories.length > 0 && selectedCategories.length === categories.length;
  const toggleCategory = (code) => setSelectedCategories(prev =>
    prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
  );
  const toggleAllCategories = () => setSelectedCategories(prev =>
    prev.length === categories.length ? [] : categories.map(c => c.code)
  );

  // Summary pills for results
  const timeSummaryText = useMemo(() => {
    const hasStart = isValid24HourTime(timeWindow.startTime);
    const hasEnd = isValid24HourTime(timeWindow.endTime);
    if (hasStart && hasEnd) return `${timeWindow.startTime} – ${timeWindow.endTime}`;
    if (hasStart) return `From ${timeWindow.startTime}`;
    if (hasEnd) return `Until ${timeWindow.endTime}`;
    return '';
  }, [timeWindow.startTime, timeWindow.endTime]);

  const allTextCombined = activeQuestions
    ? activeQuestions.filter(q => q.question_type === 'text').map(q => textAnswers[q.step_key] || '').filter(Boolean).join(' ')
    : '';
  const focusSummaryText = allTextCombined.trim().length > 30
    ? `${allTextCombined.trim().slice(0, 30)}...`
    : allTextCombined.trim();

  const renderStepIndicator = () => (
    <div className="wizard-steps" aria-label="Wizard steps">
      {Array.from({ length: totalSteps }, (_, idx) => {
        const isCompleted = showResults || stepIndex > idx;
        const isActive = !showResults && stepIndex === idx;
        return (
          <div key={`step-wrap-${idx}`} className="wizard-step-wrap">
            <div
              className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`.trim()}
            >
              {idx + 1}
            </div>
            {idx < totalSteps - 1 && (
              <div className={`wizard-step-line ${isCompleted ? 'completed' : ''}`.trim()} />
            )}
          </div>
        );
      })}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="agenda-wizard-overlay" onClick={onClose} />
      <aside
        className="agenda-wizard-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Agenda Wizard"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="agenda-wizard-header">
          <button
            type="button"
            className="agenda-wizard-close"
            onClick={onClose}
            aria-label="Close agenda wizard"
          >
            ×
          </button>
          <h2 className="agenda-wizard-title">🪄 Agenda Wizard</h2>
          <p className="agenda-wizard-subtitle">Build your personalized Learning Day</p>
        </div>

        <div className="agenda-wizard-body">
          {!showResults && renderStepIndicator()}

          {/* Loading state */}
          {!showResults && activeQuestions === null && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              Loading questions...
            </div>
          )}

          {/* Categories step */}
          {!showResults && currentQuestion?.question_type === 'categories' && (
            <>
              <div className="wizard-label">{currentQuestion.label}</div>
              <div className="wizard-sublabel">{currentQuestion.sublabel}</div>
              <div className="wizard-category-list">
                <label className={`wizard-category-item ${allCategoriesSelected ? 'selected' : ''}`.trim()}>
                  <input
                    type="checkbox"
                    checked={allCategoriesSelected}
                    onChange={toggleAllCategories}
                  />
                  <span className="wizard-color-dot" style={{ background: 'var(--siemens-teal)' }} />
                  <span>All Categories</span>
                </label>
                {categories.map((cat) => {
                  const isSelected = selectedCategories.includes(cat.code);
                  return (
                    <label key={cat.code} className={`wizard-category-item ${isSelected ? 'selected' : ''}`.trim()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCategory(cat.code)}
                      />
                      <span className="wizard-color-dot" style={{ background: cat.color }} />
                      <span>{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {/* Time window step */}
          {!showResults && currentQuestion?.question_type === 'time_window' && (
            <>
              <div className="wizard-label">{currentQuestion.label}</div>
              <div className="wizard-sublabel">{currentQuestion.sublabel}</div>
              <div className="wizard-time-row">
                <input
                  className="wizard-time-input"
                  type="text"
                  placeholder="HH:MM"
                  value={timeWindow.startTime}
                  onChange={e => setTimeWindow(tw => ({ ...tw, startTime: normalize24HourTimeInput(e.target.value) }))}
                  inputMode="numeric"
                  maxLength={5}
                />
                <span className="wizard-time-separator">to</span>
                <input
                  className="wizard-time-input"
                  type="text"
                  placeholder="HH:MM"
                  value={timeWindow.endTime}
                  onChange={e => setTimeWindow(tw => ({ ...tw, endTime: normalize24HourTimeInput(e.target.value) }))}
                  inputMode="numeric"
                  maxLength={5}
                />
              </div>
              {hasTimeError && (
                <div className="wizard-error">Please use HH:MM format (e.g. 09:00)</div>
              )}
            </>
          )}

          {/* Free text step (q3 and any custom questions) */}
          {!showResults && currentQuestion?.question_type === 'text' && (
            <>
              <div className="wizard-label">{currentQuestion.label}</div>
              <div className="wizard-sublabel">{currentQuestion.sublabel}</div>
              <textarea
                className="wizard-textarea"
                rows={4}
                maxLength={300}
                value={textAnswers[currentQuestion.step_key] || ''}
                onChange={e => setTextAnswers(ta => ({ ...ta, [currentQuestion.step_key]: e.target.value }))}
                placeholder={currentQuestion.placeholder || ''}
              />
              <div
                className={`wizard-char-count ${(textAnswers[currentQuestion.step_key] || '').length > 280 ? 'over-limit' : ''}`.trim()}
              >
                {(textAnswers[currentQuestion.step_key] || '').length} / 300
              </div>
            </>
          )}

          {/* Results */}
          {showResults && (
            <>
              <div className="wizard-label">Your Personalized Agenda</div>
              <div className="wizard-filter-summary">
                {selectedCategories.map((code) => (
                  <span
                    key={code}
                    className="wizard-filter-pill"
                    style={{ background: categoryColors[code]?.color || '#005f99' }}
                  >
                    {categoryColors[code]?.name || code}
                  </span>
                ))}
                {timeSummaryText && (
                  <span className="wizard-filter-pill time">{timeSummaryText}</span>
                )}
                {focusSummaryText && (
                  <span className="wizard-filter-pill focus">Focus: {focusSummaryText}</span>
                )}
              </div>

              {agenda.length === 0 ? (
                <div className="agenda-empty-state">
                  <p>No sessions found matching your preferences. Try broadening your categories or time window.</p>
                  <button type="button" className="wizard-btn-secondary" onClick={resetWizard}>← Try Again</button>
                </div>
              ) : (
                <>
                  {agenda.map(({ slot, sessions: slotSessions }) => (
                    <div className="agenda-slot" key={slot}>
                      <div className="agenda-slot-header">
                        <span>🕐</span>
                        <span>{formatSlotInLocalTime(slot, slotSessions[0]?.date || '')}</span>
                      </div>

                      {slotSessions.map((session, index) => {
                        const isTopPick = index === 0;
                        const segmentName = SEGMENT_DISPLAY_NAMES[session.segment] || session.segment;
                        const categoryColor = categoryColors[session.category]?.color || '#005f99';
                        const categoryName = categoryColors[session.category]?.name || session.category;
                        const registrationUrl = session.sabaLink || session.link;
                        const rawDescription = (session.description || '').trim();
                        const shortDescription = rawDescription.length > 100
                          ? `${rawDescription.slice(0, 100)}...`
                          : rawDescription;

                        return (
                          <div className="agenda-slot-session" key={`${slot}-${session.id || session.name}-${index}`}>
                            <div className={`agenda-session-badge ${isTopPick ? 'top-pick' : 'alt'}`}>
                              {isTopPick ? '⭐ TOP PICK' : '✦ ALTERNATIVE'}
                            </div>
                            <div className="agenda-session-name">{session.name}</div>
                            <div className="agenda-session-meta">
                              <span>{session.presenter || 'TBD presenter'}</span>
                              <span>·</span>
                              <span>{segmentName || 'General'}</span>
                              <span>·</span>
                              <span className="agenda-category-pill" style={{ background: categoryColor }}>
                                ● {categoryName}
                              </span>
                            </div>
                            {shortDescription && (
                              <div className="agenda-session-desc">&quot;{shortDescription}&quot;</div>
                            )}
                            {registrationUrl ? (
                              <a
                                className="agenda-register-btn"
                                style={{ background: categoryColor }}
                                href={registrationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Register →
                              </a>
                            ) : (
                              <button type="button" className="agenda-register-btn disabled" disabled>
                                No link available
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  <div className="agenda-results-footer">
                    Recommendations based on your preferences. All session times shown in your local time where applicable.
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="agenda-wizard-footer">
          {/* Left side */}
          {!showResults && !isFirstStep && (
            <button type="button" className="wizard-btn-secondary" onClick={handleBack}>← Back</button>
          )}
          {!showResults && isFirstStep && <div />}
          {showResults && (
            <button type="button" className="wizard-btn-link" onClick={resetWizard}>← Start Over</button>
          )}

          {/* Right side */}
          {!showResults && !isLastStep && (
            <button
              type="button"
              className="wizard-btn-primary"
              onClick={handleNext}
              disabled={!activeQuestions}
            >
              Next →
            </button>
          )}
          {!showResults && isLastStep && (
            <button
              type="button"
              className="wizard-btn-primary"
              onClick={handleNext}
              disabled={hasTimeError || !activeQuestions}
            >
              Build My Agenda →
            </button>
          )}
          {showResults && (
            <button type="button" className="wizard-btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </aside>
    </>
  );
};

export default AgendaWizard;